'use client';

import { useState } from 'react';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { WaitlistModal } from '@/components/ui/WaitlistModal';
import {
  Shield,
  Lock,
  CheckCircle,
  ArrowLeft,
  FileCheck,
  Server,
  Eye,
  Key,
  UserCheck,
  AlertTriangle,
  Globe,
  Zap,
  ChevronRight
} from 'lucide-react';

export default function SecurityPage() {
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <div className="container max-w-6xl mx-auto px-4 py-4">
          <Link href="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 dark:from-gray-950 dark:via-purple-950/20 dark:to-pink-950/20" />
        <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-5" />

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-4 py-2 rounded-full mb-6">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">Enterprise-Grade Security</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-purple-900 dark:from-white dark:to-purple-200">
            Security Built for Sensitive R&D Conversations
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-3xl mx-auto">
            Your IP is your competitive advantage. We protect it with the same rigor you protect your research.
          </p>
        </div>
      </section>

      {/* Core Security Features */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Defense in Depth Architecture
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Multiple layers of security ensure your conversations never leave your control
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-semibold mb-2">End-to-End Encryption</h3>
              <p className="text-sm text-muted-foreground mb-3">
                AES-256 encryption at rest, TLS 1.3 in transit. Your conversations are encrypted before they leave your browser.
              </p>
              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                <CheckCircle className="w-3 h-3" />
                <span>FIPS 140-2 compliant</span>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4">
                <UserCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold mb-2">Zero Trust Access</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Every request verified, every session authenticated. No implicit trust, continuous verification.
              </p>
              <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                <CheckCircle className="w-3 h-3" />
                <span>SAML 2.0 & OAuth 2.0</span>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-semibold mb-2">Complete Audit Trails</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Immutable logs of every access, edit, and share. Know exactly who saw what, when.
              </p>
              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                <CheckCircle className="w-3 h-3" />
                <span>Tamper-proof logging</span>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mb-4">
                <Key className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="font-semibold mb-2">Your Keys, Your Control</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Bring your own encryption keys. Rotate on your schedule. Revoke access instantly.
              </p>
              <div className="flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400">
                <CheckCircle className="w-3 h-3" />
                <span>HSM key management</span>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="font-semibold mb-2">PII Auto-Redaction</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Automatic detection and redaction of sensitive data before it's stored or shared.
              </p>
              <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                <CheckCircle className="w-3 h-3" />
                <span>AI-powered detection</span>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-pink-600 dark:text-pink-400" />
              </div>
              <h3 className="font-semibold mb-2">Isolated Infrastructure</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Single-tenant options available. Your data never mingles with others.
              </p>
              <div className="flex items-center gap-2 text-xs text-pink-600 dark:text-pink-400">
                <CheckCircle className="w-3 h-3" />
                <span>Private cloud deployment</span>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Compliance & Certifications */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Certified & Compliant
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We maintain the highest standards of security certification
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <FileCheck className="w-10 h-10 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-semibold mb-1">SOC 2 Type II</h3>
              <p className="text-xs text-muted-foreground">Annual audit</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-900/30 dark:to-cyan-900/30 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Globe className="w-10 h-10 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold mb-1">GDPR</h3>
              <p className="text-xs text-muted-foreground">EU compliant</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Shield className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-semibold mb-1">ISO 27001</h3>
              <p className="text-xs text-muted-foreground">Certified ISMS</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-yellow-100 dark:from-orange-900/30 dark:to-yellow-900/30 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Zap className="w-10 h-10 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="font-semibold mb-1">HIPAA</h3>
              <p className="text-xs text-muted-foreground">Healthcare ready</p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Practices */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Security is a Practice, Not a Feature
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our security team comes from defense, finance, and healthcare—industries where security failures aren't options
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2">Quarterly Penetration Testing</h3>
                  <p className="text-sm text-muted-foreground">
                    Third-party security firms attempt to breach our systems every quarter. Results are shared with enterprise customers.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2">Bug Bounty Program</h3>
                  <p className="text-sm text-muted-foreground">
                    We pay security researchers to find vulnerabilities before malicious actors do. Over $500K paid out to date.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2">24/7 Security Operations Center</h3>
                  <p className="text-sm text-muted-foreground">
                    Round-the-clock monitoring by security professionals. Average incident response time: under 15 minutes.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-2">Employee Security Training</h3>
                  <p className="text-sm text-muted-foreground">
                    Every employee undergoes security training quarterly. Social engineering tests monthly. Zero tolerance for failures.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready for a Security Deep Dive?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Schedule a security review with our CISO. Bring your security team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/book-transformation">
              <Button size="lg" variant="secondary" className="px-8 py-6 text-lg">
                Schedule Security Review
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="px-8 py-6 text-lg bg-transparent text-white border-white hover:bg-white hover:text-purple-600">
              Download Security Whitepaper
            </Button>
          </div>
        </div>
      </section>

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
        source="security_page"
      />
    </div>
  );
}